import { useEffect } from 'react'
import type { MessagePayload } from 'firebase/messaging'
import { toast } from 'sonner'
import {
  isFcmConfigured,
  showFcmOsNotification,
  subscribeFcmForegroundMessages,
  unsubscribeFcmForegroundMessages,
} from '@/services/fcm'
import { syncFcmDeviceRegistration } from '@/modules/notifications/utils/fcm-lifecycle'
import { ensureNotificationPermission } from '@/modules/notifications/utils/notification-permission'
import { useAuthStore } from '@/store/auth.store'

/**
 * Authenticated-shell FCM lifecycle:
 * - Ensures notification permission once (shared with VAPID — no double prompt)
 * - When granted → token refresh + register for current user
 * - Re-syncs on tab focus/visibility (token rotation after first push)
 * - Re-syncs if permission flips to granted later
 * - Foreground onMessage → OS notification + short in-app toast
 */
/** Minimum gap between focus-triggered token re-syncs. */
const FOCUS_RESYNC_MIN_MS = 10 * 60_000

export function useFcmClient() {
  const userId = useAuthStore((s) => s.session?.user.id)

  useEffect(() => {
    if (!userId) return
    if (!isFcmConfigured()) return
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return

    let cancelled = false
    let resyncTimer: ReturnType<typeof setTimeout> | null = null
    // Focus / app-switch re-checks at most every 10 min (not on every focus).
    let lastFocusSync = Date.now()

    async function syncIfGranted(force = false) {
      if (cancelled) return
      if (Notification.permission !== 'granted') return
      await syncFcmDeviceRegistration(userId!, force ? { force: true } : undefined)
    }

    function scheduleResync(fromFocus = false) {
      if (cancelled) return
      if (fromFocus) {
        if (Date.now() - lastFocusSync < FOCUS_RESYNC_MIN_MS) return
        lastFocusSync = Date.now()
      }
      if (resyncTimer) clearTimeout(resyncTimer)
      // Debounce hard — rapid focus must not stampede register calls.
      resyncTimer = setTimeout(() => {
        void syncIfGranted(true)
      }, 2_500)
    }

    async function run() {
      const permission = await ensureNotificationPermission()
      if (cancelled) return

      if (permission !== 'granted') {
        console.warn(
          `[fcm] Notification permission is "${permission}" on this browser — OS push will not register here until Allow is set for this site.`,
        )
        return
      }

      await syncIfGranted(true)
      if (cancelled) return

      await subscribeFcmForegroundMessages((payload: MessagePayload) => {
        const data = payload.data || {}
        const title = payload.notification?.title || data.title || 'Update'
        const body = payload.notification?.body || data.body || ''
        console.info('[fcm] foreground message received', { title, body })
        void showFcmOsNotification(payload)
        // Visible proof that FCM delivered while this tab is focused.
        toast(title, { description: body || undefined, duration: 5000 })
        scheduleResync()
      })
    }

    void run()

    const onVisible = () => {
      if (document.visibilityState === 'visible') scheduleResync(true)
    }
    const onFocus = () => scheduleResync(true)

    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onFocus)

    let permStatus: PermissionStatus | null = null
    const onPermChange = () => {
      void syncIfGranted(true)
    }

    if (navigator.permissions?.query) {
      void navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          if (cancelled) return
          permStatus = status
          status.addEventListener('change', onPermChange)
        })
        .catch(() => {})
    }

    return () => {
      cancelled = true
      if (resyncTimer) clearTimeout(resyncTimer)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onFocus)
      permStatus?.removeEventListener('change', onPermChange)
      unsubscribeFcmForegroundMessages()
    }
  }, [userId])
}
